/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { jsx } from '@compiled/react';
import uuid from 'uuid/v4';

import {
	type Nudge,
	useAINudges,
	useAISurfaceMetadata,
	useViewAIActions,
} from '@atlassian/ai-smart-button-api';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { useRovoButtonEventsInstrumentation } from '../../../../../hooks/useRovoButtonEventsInstrumentation';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { useFetchNudgeThrottlingState } from '../../../hooks/useFetchNudgeThrottlingState';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { NudgeContent } from '../../nudge-content';

const NUDGE_DISMISS_TIMEOUT = 30000;
const ANIMATION_DURATION = 200;

export const AIProactiveNudge = () => {
	const { fireRovoButtonUIEvent } = useRovoButtonEventsInstrumentation();
	const { changeView } = useViewAIActions();
	const surfaceMetadata = useAISurfaceMetadata();
	const [triggerId] = useState(() => uuid());
	const [isNudgeVisible, setIsNudgeVisible] = useState(false);
	const [dismissedIds, setDismissedIds] = useState<string[]>([]);
	const selectedNudges = useAINudges();

	const [acceptedIds, setAcceptedIds] = useState<string[]>([]);
	const [prevNudge, setPrevNudge] = useState<Nudge | null | undefined>(null);
	const [isMouseHovering, setIsMouseHovering] = useState<boolean>(false);
	const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);

	const potentialActiveNudge = useMemo(() => {
		/**
		 * Nudge must not be previously dismissed or accepted
		 * to be considered active.
		 */
		return selectedNudges.find(
			(nudge) => !dismissedIds.includes(nudge.id) && !acceptedIds.includes(nudge.id),
		);
	}, [selectedNudges, dismissedIds, acceptedIds]);

	const { isNudgeThrottled, isLoading: isNudgeThrottlingStateLoading } =
		useFetchNudgeThrottlingState({
			triggerId,
			hasNudgeToShow: !!potentialActiveNudge,
		});

	const activeNudge = useMemo(() => {
		if (isNudgeThrottlingStateLoading || !potentialActiveNudge) {
			return null;
		}

		if (isNudgeThrottled) {
			setDismissedIds((ids) => [...ids, potentialActiveNudge.id]);
			const nudgeId = `${potentialActiveNudge.title} - ${potentialActiveNudge.id}`;
			fireRovoButtonUIEvent({
				source: surfaceMetadata.contentType,
				action: 'throttled',
				actionSubject: 'rovoButton',
				actionSubjectId: 'aiRovoProactiveNudge',
				attributes: {
					pageMode: surfaceMetadata.pageMode,
					id: potentialActiveNudge.id,
					nudgeId: nudgeId,
				},
				product: surfaceMetadata.product,
			});
			return null;
		}

		return potentialActiveNudge;
	}, [
		potentialActiveNudge,
		isNudgeThrottlingStateLoading,
		isNudgeThrottled,
		fireRovoButtonUIEvent,
		surfaceMetadata,
	]);

	useEffect(() => {
		if (activeNudge !== prevNudge) {
			setPrevNudge(activeNudge);
			if (activeNudge && activeNudge?.id !== prevNudge?.id) {
				const nudgeId = `${activeNudge.title} - ${activeNudge.id}`;
				fireRovoButtonUIEvent({
					source: surfaceMetadata.contentType,
					action: 'rendered',
					actionSubject: 'rovoButton',
					actionSubjectId: 'aiRovoProactiveNudge',
					attributes: {
						pageMode: surfaceMetadata.pageMode,
						id: activeNudge.id,
						nudgeId,
					},
					product: surfaceMetadata.product,
				});
				window.requestAnimationFrame(() => {
					/**
					 * Needs to be set to visible in next tick, otherwise
					 * it will not animate
					 */
					setIsNudgeVisible(true);
				});
			}
		}
	}, [activeNudge, prevNudge, fireRovoButtonUIEvent, surfaceMetadata]);

	const onDismissClick = useCallback(() => {
		if (activeNudge) {
			setDismissedIds((ids) => {
				return [...ids, activeNudge.id];
			});

			const nudgeId = `${activeNudge.title} - ${activeNudge.id}`;
			fireRovoButtonUIEvent({
				source: surfaceMetadata.contentType,
				action: 'dismissed',
				actionSubject: 'rovoButton',
				actionSubjectId: 'aiRovoProactiveNudge',
				attributes: {
					pageMode: surfaceMetadata.pageMode,
					id: activeNudge.id,
					dismissedMethod: 'manual',
					nudgeId,
				},
				product: surfaceMetadata.product,
			});
		}
	}, [activeNudge, fireRovoButtonUIEvent, surfaceMetadata]);

	const onNudgeClick = useCallback(() => {
		if (activeNudge) {
			const nudgeId = `${activeNudge.title} - ${activeNudge.id}`;
			if (activeNudge.viewConfig) {
				changeView(activeNudge);
			}
			activeNudge.onClick?.();

			setAcceptedIds((ids) => {
				return [...ids, activeNudge.id];
			});

			setDismissedIds((ids) => {
				return [...ids, activeNudge.id];
			});

			fireRovoButtonUIEvent({
				source: surfaceMetadata.contentType,
				action: 'clicked',
				actionSubject: 'rovoButton',
				actionSubjectId: 'aiRovoProactiveNudge',
				attributes: {
					pageMode: surfaceMetadata.pageMode,
					id: activeNudge.id,
					nudgeId,
				},
				product: surfaceMetadata.product,
			});
		}
	}, [activeNudge, changeView, fireRovoButtonUIEvent, surfaceMetadata]);

	const onWrapperMouseEnter = useCallback(() => {
		setIsMouseHovering(true);
		const nudgeId = activeNudge ? `${activeNudge.title} - ${activeNudge.id}` : '';

		hoverTimerRef.current = setTimeout(() => {
			fireRovoButtonUIEvent({
				source: surfaceMetadata.contentType,
				action: 'hovered',
				actionSubject: 'rovoButton',
				actionSubjectId: 'aiRovoProactiveNudge',
				attributes: {
					product: surfaceMetadata.product,
					productPage: surfaceMetadata.contentType,
					pageMode: surfaceMetadata.pageMode,
					nudgeId,
				},
				product: surfaceMetadata.product,
			});
		}, 1000);
	}, [fireRovoButtonUIEvent, surfaceMetadata, activeNudge]);

	const onWrapperMouseLeave = useCallback(() => {
		setIsMouseHovering(false);

		if (hoverTimerRef.current) {
			clearTimeout(hoverTimerRef.current);
			hoverTimerRef.current = null;
		}
	}, []);

	useEffect(() => {
		let timeoutId: NodeJS.Timeout | undefined;
		let slideoutTimeoutId: NodeJS.Timeout | undefined;
		if (activeNudge && !isMouseHovering) {
			timeoutId = setTimeout(() => {
				setDismissedIds((ids) => [...ids, activeNudge.id]);
				fireRovoButtonUIEvent({
					source: surfaceMetadata.contentType,
					action: 'dismissed',
					actionSubject: 'rovoButton',
					actionSubjectId: 'aiRovoProactiveNudge',
					attributes: {
						pageMode: surfaceMetadata.pageMode,
						id: activeNudge.id,
						timerDuration: NUDGE_DISMISS_TIMEOUT,
						dismissedMethod: 'timer_expired',
					},
					product: surfaceMetadata.product,
				});
			}, NUDGE_DISMISS_TIMEOUT);

			slideoutTimeoutId = setTimeout(() => {
				setIsNudgeVisible(false);
			}, NUDGE_DISMISS_TIMEOUT - ANIMATION_DURATION);
		}

		/**
		 * On mouse enter, isMouseHovering is set to true, will trigger
		 *  cleanup function to clear timeout.
		 */
		return () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
			if (slideoutTimeoutId) {
				clearTimeout(slideoutTimeoutId);
			}
		};
	}, [
		activeNudge,
		fireRovoButtonUIEvent,
		isMouseHovering,
		surfaceMetadata.contentType,
		surfaceMetadata.pageMode,
		surfaceMetadata.product,
	]);

	if (!activeNudge) {
		return null;
	}

	return (
		<NudgeContent
			activeNudge={activeNudge}
			isNudgeVisible={isNudgeVisible}
			onWrapperMouseEnter={onWrapperMouseEnter}
			onWrapperMouseLeave={onWrapperMouseLeave}
			onNudgeClick={onNudgeClick}
			onDismissClick={onDismissClick}
		/>
	);
};
